"use client";
/* eslint-disable @next/next/no-img-element */
import { SUPPORTED_LANGS, useLanguage } from "@/utils/LanguageContext";
import { useState, useRef, useEffect } from "react";

const FLAG_SRC = {
  en: "https://flagcdn.com/w20/gb.png",
  ar: "https://flagcdn.com/w20/sa.png",
  ko: "https://flagcdn.com/w20/kr.png",
  ph: "https://flagcdn.com/w20/ph.png",
  hi: "https://flagcdn.com/w20/in.png",
  ur: "https://flagcdn.com/w20/pk.png",
  ru: "https://flagcdn.com/w20/ru.png",
  id: "https://flagcdn.com/w20/id.png",
  zh: "https://flagcdn.com/w20/cn.png",
  fr: "https://flagcdn.com/w20/fr.png",
  es: "https://flagcdn.com/w20/es.png",
  it: "https://flagcdn.com/w20/it.png",
  de: "https://flagcdn.com/w20/de.png",
} as const;

const LABELS = {
  en: "English",
  ar: "العربية",
  ko: "한국어",
  ph: "Filipino",
  hi: "हिन्दी",
  ur: "اردو",
  ru: "Русский",
  id: "Bahasa Indonesia",
  zh: "中文",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  de: "Deutsch",
} as const;

const SHORT_LABELS: Record<(typeof SUPPORTED_LANGS)[number], string> = {
  en: "EN",
  ar: "AR",
  ko: "KO",
  ph: "PH",
  hi: "HI",
  ur: "UR",
  ru: "RU",
  id: "ID",
  zh: "ZH",
  fr: "FR",
  es: "ES",
  it: "IT",
  de: "DE",
};

const SWITCHER_LANGS = SUPPORTED_LANGS.filter((code) => code !== "ko");

const LanguageSwitcher = () => {
  const { lang, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // close the dropdown when clicking outside
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectLang = (l: (typeof SUPPORTED_LANGS)[number]) => {
    changeLanguage(l);
    setIsOpen(false);
  };

  return (
    <div ref={ref} className="language-switcher">
      <button
        type="button"
        className="language-switcher__button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Current language: ${LABELS[lang]}. Change language`}
      >
        <img
          src={FLAG_SRC[lang]}
          alt={LABELS[lang]}
          className="language-switcher__flag"
          width={18}
          height={14}
        />
        <span className="language-switcher__code" aria-hidden="true">
          {SHORT_LABELS[lang]}
        </span>
        <svg className="language-switcher__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="language-switcher__menu" role="menu">
          {SWITCHER_LANGS.map((code) => (
            <button
              type="button"
              key={code}
              className={`language-switcher__item ${code === lang ? "is-active" : ""}`}
              onClick={() => selectLang(code)}
              aria-current={code === lang ? "true" : undefined}
            >
              <img
                src={FLAG_SRC[code]}
                alt={LABELS[code]}
                className="language-switcher__flag"
                width={18}
                height={14}
              />
              <span className="language-switcher__label">{LABELS[code]}</span>
            </button>
          ))}
        </div>
      )}

      <style jsx>{`
        .language-switcher {
          position: relative;
          display: inline-block;
          --switcher-button-bg: linear-gradient(
            180deg,
            rgba(21, 47, 92, 0.58) 0%,
            rgba(10, 29, 61, 0.74) 100%
          );
          --switcher-button-hover-bg: linear-gradient(
            180deg,
            rgba(29, 63, 118, 0.76) 0%,
            rgba(12, 35, 72, 0.88) 100%
          );
          --switcher-panel-bg: linear-gradient(
            180deg,
            rgba(7, 18, 42, 0.94) 0%,
            rgba(11, 28, 58, 0.92) 100%
          );
          --switcher-panel-solid: rgba(9, 22, 48, 0.96);
          --switcher-panel-border: rgba(184, 214, 255, 0.22);
          --switcher-panel-border-strong: rgba(212, 229, 255, 0.34);
          --switcher-item-hover-bg: linear-gradient(
            135deg,
            rgba(39, 88, 178, 0.42) 0%,
            rgba(13, 33, 72, 0.92) 100%
          );
          --switcher-item-active-bg: linear-gradient(
            135deg,
            rgba(68, 126, 226, 0.5) 0%,
            rgba(15, 39, 84, 0.98) 100%
          );
          --switcher-panel-shadow:
            0 20px 44px rgba(2, 8, 24, 0.46),
            0 0 0 1px rgba(112, 157, 238, 0.08);
        }

        .language-switcher__button {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-height: 40px;
          padding: 8px 12px;
          color: var(--tg-button-text-color);
          background: var(--switcher-button-bg);
          border: 1px solid var(--switcher-panel-border);
          border-radius: 999px;
          backdrop-filter: blur(calc(var(--glass-blur) + 2px)) saturate(145%);
          -webkit-backdrop-filter: blur(calc(var(--glass-blur) + 2px)) saturate(145%);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            0 10px 24px rgba(2, 8, 24, 0.2);
          transition:
            transform 0.05s ease,
            box-shadow 0.25s ease,
            border-color 0.25s ease,
            background 0.25s ease;
        }

        .language-switcher__button:hover {
          transform: translateY(-1px);
          border-color: var(--switcher-panel-border-strong);
          background: var(--switcher-button-hover-bg);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            0 14px 30px rgba(4, 12, 32, 0.28);
        }

        .language-switcher__button:focus-visible,
        .language-switcher__item:focus-visible {
          outline: none;
          border-color: rgba(217, 233, 255, 0.75);
          box-shadow:
            0 0 0 3px rgba(123, 175, 255, 0.22),
            0 14px 30px rgba(4, 12, 32, 0.3);
        }

        .language-switcher__flag {
          width: 18px;
          height: auto;
          border-radius: 3px;
          flex-shrink: 0;
        }

        .language-switcher__code {
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: var(--tg-button-text-color);
        }

        .language-switcher__label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--tg-button-text-color);
        }

        .language-switcher__icon {
          width: 0.95rem;
          height: 0.95rem;
          fill: none;
          stroke: currentColor;
          stroke-width: 2;
          color: var(--tg-button-text-color);
        }

        .language-switcher__menu {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          min-width: 190px;
          padding: 6px;
          background: var(--switcher-panel-bg);
          backdrop-filter: blur(calc(var(--glass-blur) + 4px)) saturate(155%);
          -webkit-backdrop-filter: blur(calc(var(--glass-blur) + 4px)) saturate(155%);
          border: 1px solid var(--switcher-panel-border);
          border-radius: 14px;
          box-shadow: var(--switcher-panel-shadow);
          color: var(--tg-button-text-color);
          z-index: 10;
          display: grid;
          gap: 2px;
          overflow: hidden;
        }

        .language-switcher__item {
          display: flex;
          align-items: center;
          gap: 0.7rem;
          width: 100%;
          padding: 8px 10px;
          font-size: 0.875rem;
          font-weight: 500;
          text-align: left;
          color: var(--tg-button-text-color);
          cursor: pointer;
          transition:
            color 0.2s ease,
            background 0.25s ease,
            border-color 0.25s ease,
            box-shadow 0.25s ease,
            transform 0.05s ease;
          border-radius: 10px;
          border: 1px solid transparent;
          background: transparent;
        }

        .language-switcher__item:hover {
          color: var(--tg-button-text-color);
          background: var(--switcher-item-hover-bg);
          border-color: rgba(172, 206, 255, 0.22);
          box-shadow: 0 10px 24px rgba(6, 18, 47, 0.28);
          transform: translateY(-1px);
        }

        .language-switcher__item.is-active {
          color: #f7fbff;
          background: var(--switcher-item-active-bg);
          border-color: var(--switcher-panel-border-strong);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.12),
            0 12px 28px rgba(6, 18, 47, 0.32);
        }

        .language-switcher__menu:before {
          content: "";
          position: absolute;
          right: 18px;
          top: -7px;
          width: 14px;
          height: 14px;
          background: var(--switcher-panel-solid);
          border-left: 1px solid var(--switcher-panel-border);
          border-top: 1px solid var(--switcher-panel-border);
          transform: rotate(45deg);
          z-index: -1;
          backdrop-filter: blur(calc(var(--glass-blur) + 4px)) saturate(155%);
          -webkit-backdrop-filter: blur(calc(var(--glass-blur) + 4px)) saturate(155%);
        }

        @media (max-width: 1199px) {
          .language-switcher__button {
            padding: 7px 11px;
          }

          .language-switcher__menu {
            min-width: 176px;
          }
        }
      `}</style>
    </div>
  );
};

export default LanguageSwitcher;
